import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getNotificationRoute } from '../utils/notificationNav';
import { handleActivateKey } from '../utils/a11y';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { translateNotification } from '../utils/i18nHelpers';
import PageHeader from '../components/ui/PageHeader';
import { StatCard } from '../components/ui/Card';
import Card from '../components/ui/Card';
import Alert from '../components/ui/Alert';
import Button from '../components/ui/Button';
import Skeleton from '../components/ui/Skeleton';
import OnboardingChecklist from '../components/onboarding/OnboardingChecklist';
import ExecutiveDashboard from '../components/dashboard/ExecutiveDashboard';
import PortfolioDashboard from '../components/dashboard/PortfolioDashboard';
import PersonalComplianceWidget from '../components/dashboard/PersonalComplianceWidget';

const useFormatRelativeTime = () => {
  const { t, i18n } = useTranslation();
  return (date) => {
    const d = new Date(date);
    const diffMs = Date.now() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return t('notifications.justNow');
    if (diffMin < 60) return t('notifications.minutesAgo', { count: diffMin });
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return t('notifications.hoursAgo', { count: diffH });
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return t('notifications.daysAgo', { count: diffD });
    return d.toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'ro-RO');
  };
};

// SVG icons for stat cards
const BuildingIcon = (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"
    />
  </svg>
);

const UsersIcon = (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
    />
  </svg>
);

const BookIcon = (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
    />
  </svg>
);

const CheckCircleIcon = (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const ClockIcon = (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const BookOpenIcon = (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
    />
  </svg>
);

const ChartIcon = (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
    />
  </svg>
);

const ActivityIcon = (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formatRelativeTime = useFormatRelativeTime();
  const { user } = useAuth();

  const handleNotificationClick = async (notif) => {
    if (!notif.read) {
      try {
        await api.put(`/notifications/${notif.id}/read`);
      } catch (_) {}
    }
    // Pentru notificările de tip "Declarație de semnat", verificăm status-ul:
    //   - PENDING → dashboard (popup blocant)
    //   - APPROVED → certificate (vezi fișa actualizată)
    if (notif.type === 'CHANGE_EMPLOYEE_SIGN' && notif.data?.changeLogId) {
      try {
        const res = await api.get(`/change-logs/${notif.data.changeLogId}`);
        navigate(res.data?.status === 'APPROVED' ? '/my-certificates' : '/dashboard');
        return;
      } catch (_) {
        navigate('/dashboard');
        return;
      }
    }
    navigate(getNotificationRoute(notif));
  };
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(true);
  const [issues, setIssues] = useState(null);
  // Sumar pentru tot ce așteaptă semnătura mea: instruiri (teste IIG/ILM), rapoarte ITM,
  // declarații schimbare nume/traseu. Evidențiat sus, sub onboarding — userii care semnează mai
  // mult decât simpli angajați (admin firmă, CLM, SSM, PSI) au nevoie să vadă imediat dacă au
  // de făcut ceva. Anterior dashboard-ul nu arăta nimic „de făcut" deși existau notificări.
  const [pendingSummary, setPendingSummary] = useState(null);

  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchRecentNotifications();
    fetchIssues();
    fetchPendingSignatures();
  }, []);

  const fetchPendingSignatures = async () => {
    try {
      const res = await api.get('/signatures/my-pending-summary');
      setPendingSummary(res.data || null);
    } catch (err) {
      // Log explicit ca să vedem în consola browserului dacă endpoint-ul eșuează (anterior silent → invizibil).
      console.error(
        'Pending signatures fetch failed:',
        err.response?.status,
        err.response?.data || err.message
      );
      setPendingSummary(null);
    }
  };

  const fetchIssues = async () => {
    try {
      const res = await api.get('/issues');
      setIssues(res.data);
    } catch (_) {
      /* nu afișăm widget dacă userul n-are acces */
    }
  };

  const fetchRecentNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      const list = Array.isArray(res.data) ? res.data : res.data?.notifications || [];
      setRecentNotifications(list.slice(0, 5));
    } catch {
      setRecentNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/stats/me');
      setStats(response.data);
    } catch (err) {
      console.error('Stats error:', err);
      setError(err.message);

      // ✅ Setează stats default pentru a nu bloca UI
      setStats({
        employees: 0,
        courses: 0,
        trainingsInProgress: 0,
        trainingsCompleted: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton variant="title" className="mb-2" />
          <Skeleton variant="text" className="w-1/2" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Skeleton variant="stat-card" />
          <Skeleton variant="stat-card" />
          <Skeleton variant="stat-card" />
          <Skeleton variant="stat-card" />
        </div>
        <Skeleton variant="card" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <PageHeader title={t('nav.dashboard')} />
        <Alert type="error">
          <h3 className="font-semibold mb-1">{t('dashboard.statsError')}</h3>
          <p className="text-sm">{error}</p>
          <div className="mt-3">
            <Button variant="danger" size="sm" onClick={fetchStats}>
              {t('dashboard.tryAgain')}
            </Button>
          </div>
        </Alert>
      </div>
    );
  }

  // ✅ Verifică că stats există înainte de a le folosi
  if (!stats) {
    return (
      <div className="space-y-4">
        <PageHeader title={t('nav.dashboard')} />
        <Alert type="warning">{t('dashboard.statsUnavailable')}</Alert>
      </div>
    );
  }

  // Statistici diferite pe rol. Folosim optional chaining (`stats?.`) și fallback `?? 0`
  // peste tot — la primul render, sau dacă endpointul răspunde cu câmpuri lipsă, evităm
  // „Cannot read properties of null (reading '...')". `user?.role` previne crash dacă userul
  // nu e încă populat în context.
  const getStatsCards = () => {
    if (user?.role === 'SUPER_ADMIN') {
      return [
        { label: t('dashboard.activeCompanies'), value: stats?.companies ?? 0, icon: BuildingIcon },
        { label: t('dashboard.totalEmployees'), value: stats?.employees ?? 0, icon: UsersIcon },
        { label: t('dashboard.activeCourses'), value: stats?.courses ?? 0, icon: BookIcon },
        {
          label: t('dashboard.completedTrainings'),
          value: stats?.trainingsCompleted ?? 0,
          icon: CheckCircleIcon,
        },
      ];
    }

    if (user?.role === 'COMPANY_ADMIN') {
      return [
        { label: t('dashboard.employees'), value: stats?.employees ?? 0, icon: UsersIcon },
        {
          label: t('dashboard.availableCourses'),
          value: stats?.coursesAvailable ?? 0,
          icon: BookIcon,
        },
        {
          label: t('dashboard.inProgressTrainings'),
          value: stats?.trainingsInProgress ?? 0,
          icon: ClockIcon,
        },
        {
          label: t('dashboard.completed'),
          value: stats?.trainingsCompleted ?? 0,
          icon: CheckCircleIcon,
        },
      ];
    }

    // EMPLOYEE
    return [
      {
        label: t('dashboard.assignedCourses'),
        value: stats?.assignedCourses ?? 0,
        icon: BookOpenIcon,
      },
      {
        label: t('dashboard.completed'),
        value: stats?.completedCourses ?? 0,
        icon: CheckCircleIcon,
      },
      { label: t('dashboard.inProgress'), value: stats?.inProgressCourses ?? 0, icon: ClockIcon },
      {
        label: t('dashboard.averageScore'),
        value: `${stats?.averageScore ?? 0}%`,
        icon: ChartIcon,
      },
    ];
  };

  const getSubtitle = () => {
    if (user?.role === 'SUPER_ADMIN') return t('dashboard.subtitleSuperAdmin');
    if (user?.role === 'DIRECTOR_GENERAL')
      return t('dashboard.subtitleDirectorGeneral', { company: user.companyName });
    if (user?.role === 'COMPANY_ADMIN')
      return t('dashboard.subtitleCompanyAdmin', { company: user.companyName });
    if (user?.role === 'SSM_COMPANY_ADMIN')
      return t('dashboard.subtitleProvider', { company: user.companyName });
    if (user?.role === 'INSTRUCTOR' || user?.role === 'SSM_INSTRUCTOR')
      return t('dashboard.subtitleInstructor', { company: user.companyName });
    if (user?.role === 'EMPLOYEE')
      return t('dashboard.subtitleEmployee', { company: user.companyName });
    return '';
  };

  // Vedere executivă pe firma proprie (conformitate) — DIRECTOR_GENERAL + COMPANY_ADMIN.
  const showExecutive = user?.role === 'DIRECTOR_GENERAL' || user?.role === 'COMPANY_ADMIN';
  // Vedere portofoliu pe firmele client — provider SSM.
  const showPortfolio = user?.role === 'SSM_COMPANY_ADMIN' || user?.role === 'SSM_INSTRUCTOR';

  const statsCards = getStatsCards();

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title={t('dashboard.hello', { name: user?.firstName })}
        subtitle={getSubtitle()}
        actions={
          user?.role !== 'SUPER_ADMIN' ? (
            <Button variant="primary" onClick={() => navigate('/my-courses')}>
              {BookOpenIcon}
              {t('dashboard.goToMyCourses')}
            </Button>
          ) : null
        }
      />

      {/* Checklist persistent de onboarding — apare doar dacă userul a finalizat wizardul
          dar mai are pași sariți sau pași noi (introduși după ce s-a onboardat). Dispare singur. */}
      <OnboardingChecklist />

      {/* Widget conformitate PERSONALĂ („Sunt eu în regulă?" + „Următorul pas" + scadențe).
          Se auto-ascunde pentru userii fără profil de angajat sau fără nimic de monitorizat.
          Util pentru EMPLOYEE și pentru adminii standalone care sunt și propriul angajat. */}
      <PersonalComplianceWidget />

      {/* Card prominent: ai semnături de făcut.
          UI simplu — total + un singur click direct la pagina cu fișele de semnat.
          Vizibil pentru oricine are signing duties (COMPANY_ADMIN, SSM, CLM, Cadru PSI, etc.)
          — randat ÎNAINTE de ExecutiveDashboard/PortfolioDashboard ca să apară pentru toți. */}
      {pendingSummary && pendingSummary.total > 0 && (
        <button
          type="button"
          aria-label={t('dashboard.ariaViewSheetsToSign')}
          onClick={() => navigate('/sheets-to-sign')}
          className="w-full text-left rounded-lg border-2 border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-950/40 p-4 transition hover:shadow-md hover:border-amber-500 cursor-pointer"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <svg
                className="w-6 h-6 mt-0.5 flex-shrink-0 text-amber-600 dark:text-amber-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.8}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
                />
              </svg>
              <div className="min-w-0">
                <p className="text-base font-bold text-amber-900 dark:text-amber-200">
                  {t('dashboard.pendingSignaturesTitle')}
                </p>
                <p className="text-sm text-amber-800 dark:text-amber-300 mt-1">
                  {t('dashboard.pendingSignaturesIntro')}
                </p>
              </div>
            </div>
            <span className="text-sm font-semibold text-amber-700 dark:text-amber-200 hover:underline flex-shrink-0 self-center">
              {t('dashboard.pendingSignaturesAction')} →
            </span>
          </div>
        </button>
      )}

      {/* Widget Probleme + IP — afișate permanent dacă userul are scope (issues endpoint a răspuns).
          Când nu există probleme, banner-ele rămân vizibile cu mesaj de „Tot bine" — confirmă activ
          că nu ai nimic de făcut, în loc să dispară și să te lase să te întrebi dacă funcționează. */}
      {issues && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Probleme — banner cu listă scurtă */}
          <div
            role="button"
            tabIndex={0}
            aria-label={t('dashboard.ariaViewIssues')}
            onClick={() => navigate('/issues')}
            onKeyDown={(e) => handleActivateKey(e, () => navigate('/issues'))}
            className={`cursor-pointer rounded-lg border p-4 transition hover:shadow-md ${
              issues.criticalCount > 0
                ? 'border-danger-300 bg-danger-50 dark:border-danger-800 dark:bg-danger-950/30'
                : 'border-emerald-200 bg-emerald-50 dark:border-emerald-800/60 dark:bg-emerald-950/20'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex items-start gap-2.5">
                {issues.criticalCount > 0 ? (
                  <svg
                    className="w-5 h-5 mt-0.5 flex-shrink-0 text-danger-600 dark:text-danger-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.8}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5 mt-0.5 flex-shrink-0 text-emerald-600 dark:text-emerald-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.8}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                    />
                  </svg>
                )}
                <div className="min-w-0">
                  <p
                    className={`text-sm font-semibold ${issues.criticalCount > 0 ? 'text-danger-800 dark:text-danger-300' : 'text-emerald-800 dark:text-emerald-300'}`}
                  >
                    {issues.criticalCount > 0
                      ? t('dashboard.criticalIssues', { count: issues.criticalCount })
                      : t('dashboard.noCriticalIssues')}
                  </p>
                  <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                    {issues.criticalCount > 0 ? (
                      <>
                        {issues.summary?.stuckTrainings > 0 && (
                          <span>
                            {t('dashboard.issueStuckTrainings', {
                              count: issues.summary.stuckTrainings,
                            })}{' '}
                            ·{' '}
                          </span>
                        )}
                        {issues.summary?.unsignedSheets > 0 && (
                          <span>
                            {t('dashboard.issueUnsignedSheets', {
                              count: issues.summary.unsignedSheets,
                            })}{' '}
                            ·{' '}
                          </span>
                        )}
                        {issues.summary?.pendingDeclarations > 0 && (
                          <span>
                            {t('dashboard.issuePendingDeclarations', {
                              count: issues.summary.pendingDeclarations,
                            })}{' '}
                            ·{' '}
                          </span>
                        )}
                        {issues.summary?.gdprPending > 0 && (
                          <span>
                            {t('dashboard.issueGdprPending', { count: issues.summary.gdprPending })}{' '}
                            ·{' '}
                          </span>
                        )}
                        {issues.summary?.missingData > 0 && (
                          <span>
                            {t('dashboard.issueMissingData', { count: issues.summary.missingData })}
                          </span>
                        )}
                      </>
                    ) : (
                      <span>{t('dashboard.allUnderControl')}</span>
                    )}
                  </p>
                </div>
              </div>
              <span className="text-xs text-primary-600 dark:text-primary-300 hover:underline flex-shrink-0">
                {t('dashboard.viewDetails')}
              </span>
            </div>
          </div>

          {/* IP — sumar cu link la semaforul complet */}
          <div
            role="button"
            tabIndex={0}
            aria-label={t('dashboard.ariaViewIpTraffic')}
            onClick={() => navigate('/dashboard-ip')}
            onKeyDown={(e) => handleActivateKey(e, () => navigate('/dashboard-ip'))}
            className={`cursor-pointer rounded-lg border p-4 transition hover:shadow-md ${
              (issues.summary?.ipExpired || 0) > 0
                ? 'border-danger-300 bg-danger-50 dark:border-danger-800 dark:bg-danger-950/30'
                : (issues.summary?.ipExpiringSoon || 0) > 0
                  ? 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30'
                  : 'border-emerald-200 bg-emerald-50 dark:border-emerald-800/60 dark:bg-emerald-950/20'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex items-start gap-2.5">
                {/* Mini-semafor stilizat: 3 cercuri verticale, cel activ după severitate. */}
                <span className="flex flex-col gap-0.5 mt-1 flex-shrink-0" aria-hidden="true">
                  <span
                    className={`block w-2 h-2 rounded-full ${(issues.summary?.ipExpired || 0) > 0 ? 'bg-danger-500' : 'bg-slate-300 dark:bg-slate-600'}`}
                  ></span>
                  <span
                    className={`block w-2 h-2 rounded-full ${(issues.summary?.ipExpiringSoon || 0) > 0 && (issues.summary?.ipExpired || 0) === 0 ? 'bg-amber-500' : 'bg-slate-300 dark:bg-slate-600'}`}
                  ></span>
                  <span
                    className={`block w-2 h-2 rounded-full ${(issues.summary?.ipExpired || 0) + (issues.summary?.ipExpiringSoon || 0) === 0 ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`}
                  ></span>
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                    {t('dashboard.ipTraffic')}
                  </p>
                  <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                    {(issues.summary?.ipExpired || 0) + (issues.summary?.ipExpiringSoon || 0) ===
                    0 ? (
                      <span className="text-emerald-700 dark:text-emerald-300 font-medium">
                        {t('dashboard.allIpUpToDate')}
                      </span>
                    ) : (
                      <>
                        <span className="text-danger-700 dark:text-danger-300 font-medium">
                          {t('dashboard.ipExpiredCount', { count: issues.summary?.ipExpired || 0 })}
                        </span>
                        {' · '}
                        <span className="text-amber-700 dark:text-amber-300 font-medium">
                          {t('dashboard.ipExpiringSoonCount', {
                            count: issues.summary?.ipExpiringSoon || 0,
                          })}
                        </span>
                      </>
                    )}
                  </p>
                </div>
              </div>
              <span className="text-xs text-primary-600 dark:text-primary-300 hover:underline flex-shrink-0">
                {t('dashboard.viewTrafficLight')}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Vedere rol-specifică:
          - DIRECTOR_GENERAL & COMPANY_ADMIN → panou executiv (conformitate firmă proprie)
          - SSM_COMPANY_ADMIN & SSM_INSTRUCTOR → portofoliu (toate firmele client)
          - restul (EMPLOYEE, SUPER_ADMIN, INSTRUCTOR) → grila de KPI clasică */}
      {showExecutive ? (
        <ExecutiveDashboard />
      ) : showPortfolio ? (
        <PortfolioDashboard />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statsCards.map((stat) => (
            <StatCard key={stat.label} label={stat.label} value={stat.value} icon={stat.icon} />
          ))}
        </div>
      )}

      {/* Activity — ultimele notificări */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {ActivityIcon}
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {t('dashboard.recentActivity')}
            </h3>
          </div>
          <Link
            to="/notifications"
            className="text-sm text-primary-600 dark:text-primary-300 hover:text-primary-700 font-medium"
          >
            {t('dashboard.viewAll')}
          </Link>
        </div>
        {notificationsLoading ? (
          <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-6">
            {t('common.loading')}
          </p>
        ) : recentNotifications.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-6">
            {t('dashboard.noActivity')}
          </p>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {recentNotifications.map((n) => {
              const { title: nTitle, message: nMessage } = translateNotification(t, n);
              return (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => handleNotificationClick(n)}
                    onKeyDown={(e) => handleActivateKey(e, () => handleNotificationClick(n))}
                    className="w-full text-left py-2.5 flex items-start gap-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-md px-2 -mx-2 transition-colors"
                  >
                    <span
                      className={`mt-0.5 flex-shrink-0 ${n.read ? 'text-slate-400 dark:text-slate-400' : 'text-primary-600 dark:text-primary-300'}`}
                    >
                      {ActivityIcon}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm ${n.read ? 'text-slate-600 dark:text-slate-300' : 'text-slate-900 dark:text-slate-100 font-medium'} truncate`}
                      >
                        {nTitle || nMessage}
                      </p>
                      {nTitle && nMessage && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                          {nMessage}
                        </p>
                      )}
                    </div>
                    <span className="text-xs text-slate-400 dark:text-slate-400 flex-shrink-0">
                      {formatRelativeTime(n.createdAt)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
};

export default Dashboard;
